import { Helmet } from 'react-helmet-async';

interface OrganizationSchemaProps {
  name?: string;
  url?: string;
  logo?: string;
  sameAs?: string[];
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

interface RealEstateListingSchemaProps {
  name: string;
  description: string;
  url: string;
  image: string;
  price: number;
  priceCurrency?: string;
  address: {
    streetAddress: string;
    addressLocality: string; // city
    addressRegion?: string; // province
    postalCode?: string;
    addressCountry?: string;
  };
  numberOfRooms?: number;
  numberOfBathroomsTotal?: number;
  floorSize?: {
    value: number;
    unitCode: string;
  };
  yearBuilt?: number;
  propertyType?: string;
}

interface LocalBusinessSchemaProps {
  name?: string;
  description?: string;
  url?: string;
  logo?: string;
  telephone?: string;
  email?: string;
  address?: {
    streetAddress?: string;
    addressLocality: string;
    addressRegion: string;
    postalCode?: string;
    addressCountry: string;
  };
  areaServed?: string[];
  priceRange?: string;
  sameAs?: string[];
}

// Domains formally associated with the brokerage behind Book A Viewing
const BROKERAGE_SAME_AS = ["https://smartprorealty.ca"];

export function OrganizationSchema({
  name = "Book Viewing",
  url = "https://bookviewing.ca",
  logo = "https://bookviewing.ca/og-image.png",
  sameAs = BROKERAGE_SAME_AS
}: OrganizationSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name,
    url,
    logo,
    sameAs
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

export function LocalBusinessSchema({
  name = "Book Viewing",
  description = "Book home viewings online in Edmonton and Calgary. No phone calls needed.",
  url = "https://bookviewing.ca",
  logo = "https://bookviewing.ca/og-image.png",
  address = {
    addressLocality: "Edmonton",
    addressRegion: "AB",
    addressCountry: "CA"
  },
  areaServed = ["Edmonton", "Calgary"],
  priceRange = "Free",
  sameAs = BROKERAGE_SAME_AS
}: LocalBusinessSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    name,
    description,
    url,
    logo,
    address: {
      "@type": "PostalAddress",
      ...address
    },
    areaServed: areaServed.map(area => ({
      "@type": "City",
      name: area
    })),
    priceRange,
    ...(sameAs && sameAs.length > 0 && { sameAs })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

export function BreadcrumbSchema({ items }: BreadcrumbSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

export function RealEstateListingSchema({
  name,
  description,
  url,
  image,
  price,
  priceCurrency = "CAD",
  address,
  numberOfRooms,
  numberOfBathroomsTotal,
  floorSize,
  yearBuilt,
  propertyType
}: RealEstateListingSchemaProps) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name,
    description: description?.substring(0, 300) || name,
    url,
    image,
    offers: {
      "@type": "Offer",
      price,
      priceCurrency,
      availability: "https://schema.org/InStock"
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: address.streetAddress,
      addressLocality: address.addressLocality,
      addressRegion: address.addressRegion || "AB",
      postalCode: address.postalCode,
      addressCountry: address.addressCountry || "CA"
    },
    ...(numberOfRooms && { numberOfRooms }),
    ...(numberOfBathroomsTotal && { numberOfBathroomsTotal }),
    ...(floorSize && {
      floorSize: {
        "@type": "QuantitativeValue",
        value: floorSize.value,
        unitCode: floorSize.unitCode
      }
    }),
    ...(yearBuilt && { yearBuilt }),
    ...(propertyType && {
      "@type": ["RealEstateListing", propertyType === "House" ? "House" : 
                propertyType === "Condo" ? "Apartment" : 
                propertyType === "Townhouse" ? "House" : "Residence"]
    })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

interface PropertySEOProps {
  title: string;
  description: string;
  canonicalUrl: string;
  image?: string;
  ogImage?: string; // Dynamic OG image URL
  price?: number;
  address?: string;
  city?: string;
  neighborhood?: string;
  noIndex?: boolean; // Add noindex for unavailable listings
}

export function PropertyPageMeta({
  title,
  description,
  canonicalUrl,
  image,
  ogImage,
  price,
  address,
  city,
  neighborhood,
  noIndex = false,
}: PropertySEOProps) {
  // Truncate title to 60 chars, description to 160 chars  
  const seoTitle = title.length > 57 ? `${title.substring(0, 57)}...` : title;
  const seoDescription = description.length > 157 ? `${description.substring(0, 157)}...` : description;
  
  // Use property image for social sharing (Facebook requires PNG/JPG, not SVG)
  const socialImage = ogImage || image;
  
  // Format price for OG tags
  const formattedPrice = price ? new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(price) : '';
  
  // Create a rich OG title with price
  const ogTitle = price 
    ? `${formattedPrice} – ${address || title}` 
    : seoTitle;
  
  // Create location-rich description
  const location = neighborhood ? `${neighborhood}, ${city}` : city;
  const ogDescription = location 
    ? `${seoDescription} ${location ? `Located in ${location}.` : ''}`.trim()
    : seoDescription;

  return (
    <Helmet>
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <link rel="canonical" href={canonicalUrl} />
      {/* Add noindex for unavailable/sold listings to prevent Google from indexing dead pages */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {noIndex && <meta name="googlebot" content="noindex, nofollow" />}
      {socialImage && <meta property="og:image" content={socialImage} />}
      {socialImage && <meta property="og:image:width" content="1200" />}
      {socialImage && <meta property="og:image:height" content="630" />}
      {socialImage && <meta property="og:image:alt" content={ogTitle} />}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Book Viewing" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      {socialImage && <meta name="twitter:image" content={socialImage} />}
    </Helmet>
  );
}

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  faqs: FAQItem[];
}

export function FAQSchema({ faqs }: FAQSchemaProps) {
  if (!faqs || faqs.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}
